import { serverTimestamp, type FieldValue } from 'firebase/firestore'
import {
  typedCollection,
  typedDoc,
  getCollection,
  getDocument,
  updateDocument,
  deleteDocument,
  addDoc,
  orderBy,
  setDocument,
} from '@/lib/firebase/firestore'
import type { FestivalDocument, FestivalEditionDocument } from '@/types'
import type { FestivalFormValues, FestivalEditionFormValues } from '../types'

const festivalsCol = () => typedCollection<FestivalDocument>('festivals')
const festivalDoc = (id: string) => typedDoc<FestivalDocument>('festivals', id)
const editionsCol = (festivalId: string) =>
  typedCollection<FestivalEditionDocument>(`festivals/${festivalId}/editions`)
const editionDoc = (festivalId: string, editionId: string) =>
  typedDoc<FestivalEditionDocument>(`festivals/${festivalId}/editions`, editionId)

type FestivalPayload = Omit<FestivalDocument, 'id' | 'createdAt' | 'updatedAt'> & {
  createdAt: FieldValue
  updatedAt: FieldValue
}

export const adminFestivalsService = {
  async getAllFestivals(): Promise<Array<FestivalDocument & { id: string }>> {
    return getCollection(festivalsCol(), orderBy('order', 'asc'))
  },

  async getFestivalById(id: string): Promise<(FestivalDocument & { id: string }) | null> {
    const data = await getDocument(festivalDoc(id))
    if (!data) return null
    return { ...data, id }
  },

  async createFestival(data: FestivalFormValues): Promise<string> {
    const payload: FestivalPayload = {
      name: data.name,
      slug: data.slug,
      tagline: data.tagline,
      description: data.description,
      isPublished: data.isPublished,
      order: data.order,
      ...(data.logoMediaId && { logoMediaId: data.logoMediaId }),
      ...(data.logoUrl && { logoUrl: data.logoUrl }),
      ...(data.heroMediaId && { heroMediaId: data.heroMediaId }),
      ...(data.heroUrl && { heroUrl: data.heroUrl }),
      ...(data.heroFocusX !== undefined && { heroFocusX: data.heroFocusX }),
      ...(data.heroFocusY !== undefined && { heroFocusY: data.heroFocusY }),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }
    const ref = await addDoc(festivalsCol(), payload as unknown as FestivalDocument)
    return ref.id
  },

  async updateFestival(id: string, data: Partial<FestivalFormValues>): Promise<void> {
    const payload = Object.fromEntries(
      Object.entries({ ...data, updatedAt: serverTimestamp() }).filter(([, v]) => v !== undefined)
    )
    await updateDocument(festivalDoc(id), payload)
  },

  async deleteFestival(id: string): Promise<void> {
    await deleteDocument(festivalDoc(id))
  },

  async getEditions(festivalId: string): Promise<Array<FestivalEditionDocument & { id: string }>> {
    return getCollection(editionsCol(festivalId), orderBy('year', 'asc'))
  },

  async setEdition(festivalId: string, values: FestivalEditionFormValues): Promise<void> {
    const docId = String(values.year)
    await setDocument(editionDoc(festivalId, docId), {
      year: values.year,
      participants: values.participants.filter(Boolean),
      isPublished: values.isPublished,
    } as FestivalEditionDocument)
  },

  async deleteEdition(festivalId: string, editionId: string): Promise<void> {
    await deleteDocument(editionDoc(festivalId, editionId))
  },
}
